import { computeBomQuantity } from '@erp/shared';

/**
 * Cuerpo del `PATCH /api/bom-lines/:id` y reglas de qué pide cada tipo de línea.
 *
 * La respuesta trae los campos con los `numeric` de PostgreSQL como texto (`fixedQty: "2.000"`),
 * mientras que el esquema del servidor exige números. Construirlo aquí, en una función pura con
 * test, evita el 400 que solo aparece al pulsar «Guardar» en la pantalla.
 */
export interface BomLineForm {
  productId: string;
  ruleType: string;
  fixedQty: string;
  formula: string;
  wastePct: string;
  isOptional: boolean;
  optionCode: string;
  notes: string;
}

export interface BomLinePatch {
  productId: string;
  ruleType: string;
  fixedQty: number;
  formula: string | null;
  wastePct: number;
  isOptional: boolean;
  optionCode: string | null;
  notes: string | null;
}

/**
 * Qué papel juega la fórmula en cada regla. No todas la usan, y exigirla en todas es lo que dejaba
 * sin poder editar las líneas de `perimeter` y `area`: el cálculo no las mira y la pantalla las
 * pedía.
 */
export type PapelFormula = 'requerida' | 'opcional' | 'no-usada';

export function papelFormula(ruleType: string): PapelFormula {
  if (ruleType === 'formula') return 'requerida';
  // `led_length` sin fórmula usa el perímetro, que es lo habitual en una tira de LED.
  if (ruleType === 'led_length') return 'opcional';
  return 'no-usada';
}

const numero = (valor: string): number => {
  const n = Number(String(valor).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const textoOVacio = (valor: string): string | null => valor.trim() || null;

export function buildBomLinePatch(form: BomLineForm): BomLinePatch {
  const papel = papelFormula(form.ruleType);
  return {
    productId: form.productId,
    ruleType: form.ruleType,
    // La cantidad fija solo cuenta en las reglas que la usan; en el resto va a cero para no
    // arrastrar un valor viejo.
    fixedQty: form.ruleType === 'fixed' ? numero(form.fixedQty) : 0,
    formula: papel === 'no-usada' ? null : textoOVacio(form.formula),
    wastePct: Math.max(0, Math.min(100, numero(form.wastePct))),
    isOptional: form.isOptional,
    optionCode: form.isOptional ? textoOVacio(form.optionCode) : null,
    notes: textoOVacio(form.notes)
    // `priority` no se manda nunca: el detalle de la BOM no la devuelve, así que reenviarla la
    // pondría a cero en cada guardado y reordenaría la lista sin que nadie lo pida.
  };
}

/** Lo que se pone en el formulario al abrir una línea que ya existe. */
export function bomLineToForm(line: {
  productId: string; ruleType: string; fixedQty: string | number; formula: string | null;
  wastePct: string | number; isOptional: boolean; optionCode: string | null; notes: string | null;
}): BomLineForm {
  return {
    productId: line.productId,
    ruleType: line.ruleType,
    fixedQty: String(line.fixedQty ?? ''),
    formula: line.formula ?? '',
    wastePct: String(line.wastePct ?? 0),
    isOptional: line.isOptional,
    optionCode: line.optionCode ?? '',
    notes: line.notes ?? ''
  };
}

/** Prueba la fórmula con las mismas reglas que el cálculo de necesidades. */
export function formulaValida(ruleType: string, formula: string): boolean {
  if (!formula.trim()) return false;
  try {
    computeBomQuantity(
      { ruleType, fixedQty: 0, formula, wastePct: 0 },
      { widthMm: 1000, heightMm: 800, powerW: 40, configuration: {} }
    );
    return true;
  } catch {
    return false;
  }
}

/** Qué falla antes de enviar, para no gastar una petición en un 400 evitable. */
export function validateBomLineForm(form: BomLineForm): string | null {
  if (!form.productId) return 'Selecciona un componente';
  if (form.ruleType === 'fixed' && !(numero(form.fixedQty) > 0)) return 'La cantidad fija debe ser mayor que cero';
  const papel = papelFormula(form.ruleType);
  if (papel === 'requerida' && !formulaValida(form.ruleType, form.formula)) {
    return 'Esta regla necesita una fórmula válida (por ejemplo «perimeter * 1.05»)';
  }
  if (papel === 'opcional' && form.formula.trim() && !formulaValida(form.ruleType, form.formula)) {
    return 'La fórmula no es válida; déjala vacía para usar el perímetro';
  }
  if (form.isOptional && !form.optionCode.trim()) return 'Una línea opcional necesita la opción que la activa';
  const merma = numero(form.wastePct);
  if (merma < 0 || merma > 100) return 'La merma va entre 0 y 100 %';
  return null;
}
